import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import bandService from "../../../services/bandService";
import useBandCreator from "../../../hooks/useBandCreator";
import BandCreatorModal from "../../organisms/Bands/BandCreatorModal";
import useBandsCatalogData from "./hooks/useBandsCatalogData";
import useSelectedBandDetails from "./hooks/useSelectedBandDetails";
import BandsExploreView from "./views/BandsExploreView";
import SelectedBandDetailsView from "./views/SelectedBandDetailsView";
import { getBandIdentity } from "./utils/bandWelcomeTransforms";
import "./index.scss";

const markBandInviteAccepted = (band) => {
    if (!band) return band;
    return {
        ...band,
        currentUserIsMember: true,
        currentUserMembershipStatus: "ACTIVE",
        memberCount: Number.isFinite(band.memberCount) ? band.memberCount + 1 : band.memberCount,
        activeMemberCount: Number.isFinite(band.activeMemberCount) ? band.activeMemberCount + 1 : band.activeMemberCount,
    };
};

const clearBandInviteState = (band) => {
    if (!band) return band;
    return {
        ...band,
        currentUserIsMember: false,
        currentUserMembershipStatus: null,
    };
};

const replaceOrUpdateBand = (bands, bandId, replacement, updater) => (
    (Array.isArray(bands) ? bands : []).map((band) => {
        if (getBandIdentity(band) !== bandId) {
            return band;
        }
        if (replacement) {
            return { ...band, ...replacement };
        }
        return updater ? updater(band) : band;
    })
);

const prependOrReplaceBand = (bands, nextBand) => {
    const bandId = getBandIdentity(nextBand);
    if (!bandId) {
        return Array.isArray(bands) ? bands : [];
    }
    const safeBands = Array.isArray(bands) ? bands : [];
    let didReplace = false;
    const updatedBands = safeBands.map((band) => {
        if (getBandIdentity(band) !== bandId) {
            return band;
        }
        didReplace = true;
        return { ...band, ...nextBand };
    });
    return didReplace ? updatedBands : [nextBand, ...updatedBands];
};

export default function BandsWelcomePage({ onOpenMyBand, className = "" }) {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useSelector((state) => state.auth.user) || null;
    const portalRoot = React.useMemo(() => {
        if (typeof document === "undefined") {
            return null;
        }
        return document.getElementById("portal") || document.body;
    }, []);
    const [bandInvitationActionId, setBandInvitationActionId] = React.useState(null);
    const [bandInvitationError, setBandInvitationError] = React.useState(null);
    const {
        bands,
        setBands,
        catalogBands,
        myCatalogBands,
        pendingBandInvitationCatalogBands,
        pendingBandInvitationCount,
        isLoading,
        isLoadingMore,
        hasMore,
        error,
        myBandsError,
        loadMoreBands,
    } = useBandsCatalogData();
    const selectedBandDetails = useSelectedBandDetails({
        currentUser,
        location,
        navigate,
        setBands,
    });
    const { handleOpenBand } = selectedBandDetails;
    const handleBandCreated = React.useCallback(async (created) => {
        const bandId = getBandIdentity(created);
        let bandToOpen = created;
        if (bandId) {
            try {
                bandToOpen = await bandService.getBandDetails(bandId);
            } catch (error) {
                console.error("Failed to refresh created band before opening", error);
            }
        }
        if (!bandToOpen || !getBandIdentity(bandToOpen)) {
            return;
        }
        setBands(
            (current) => prependOrReplaceBand(current, bandToOpen),
            (current) => prependOrReplaceBand(current, bandToOpen),
        );
        await handleOpenBand(bandToOpen);
    }, [handleOpenBand, setBands]);
    const bandCreator = useBandCreator({ onCreated: handleBandCreated });
    const handleAcceptBandInvite = React.useCallback(async (band) => {
        const bandId = getBandIdentity(band);
        if (!bandId || bandInvitationActionId) {
            return;
        }
        setBandInvitationActionId(bandId);
        setBandInvitationError(null);
        try {
            await bandService.acceptInvitation(bandId);
            let refreshedBand = null;
            try {
                refreshedBand = await bandService.getBandDetails(bandId);
            } catch (_error) {
                refreshedBand = null;
            }
            setBands(
                (current) => replaceOrUpdateBand(current, bandId, refreshedBand, markBandInviteAccepted),
                (current) => replaceOrUpdateBand(current, bandId, refreshedBand, markBandInviteAccepted),
            );
        } catch (error) {
            setBandInvitationError(error);
        } finally {
            setBandInvitationActionId(null);
        }
    }, [bandInvitationActionId, setBands]);

    const handleRejectBandInvite = React.useCallback(async (band) => {
        const bandId = getBandIdentity(band);
        if (!bandId || bandInvitationActionId) {
            return;
        }
        setBandInvitationActionId(bandId);
        setBandInvitationError(null);
        try {
            await bandService.rejectInvitation(bandId);
            setBands(
                (current) => replaceOrUpdateBand(current, bandId, null, clearBandInviteState),
                (current) => (Array.isArray(current) ? current : [])
                    .filter((entry) => getBandIdentity(entry) !== bandId),
            );
        } catch (error) {
            setBandInvitationError(error);
        } finally {
            setBandInvitationActionId(null);
        }
    }, [bandInvitationActionId, setBands]);

    if (selectedBandDetails.selectedBand) {
        return (
            <SelectedBandDetailsView
                className={className}
                onOpenMyBand={onOpenMyBand}
                {...selectedBandDetails.detailsViewProps}
            />
        );
    }

    return (
        <>
            <BandsExploreView
                className={className}
                bands={bands}
                catalogBands={catalogBands}
                myCatalogBands={myCatalogBands}
                pendingBandInvitationCatalogBands={pendingBandInvitationCatalogBands}
                pendingBandInvitationCount={pendingBandInvitationCount}
                bandInvitationActionId={bandInvitationActionId}
                bandInvitationError={bandInvitationError}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                error={error}
                myBandsError={myBandsError}
                isRoutePreviewLoading={selectedBandDetails.isRoutePreviewLoading}
                routePreviewError={selectedBandDetails.routePreviewError}
                onLoadMore={loadMoreBands}
                onOpenBand={handleOpenBand}
                onCreateBand={bandCreator.open}
                onAcceptBandInvite={handleAcceptBandInvite}
                onRejectBandInvite={handleRejectBandInvite}
            />
            <BandCreatorModal
                portalRoot={portalRoot}
                isOpen={bandCreator.isOpen}
                onClose={bandCreator.close}
                onSubmit={bandCreator.submit}
                nameValue={bandCreator.draft.name}
                descriptionValue={bandCreator.draft.description}
                onNameChange={bandCreator.handleNameChange}
                onDescriptionChange={bandCreator.handleDescriptionChange}
                isSubmitting={bandCreator.isSubmitting}
                error={bandCreator.error}
                {...bandCreator.modalLabels}
            />
        </>
    );
}

BandsWelcomePage.propTypes = {
    onOpenMyBand: PropTypes.func.isRequired,
    className: PropTypes.string,
};
